import {ChangeDetectionStrategy, Component, computed, Injectable, input, Signal, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AlternativOgAntallModell, FargeHex, FordelingAggregatModell, RangeringValg} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatTooltip} from "@angular/material/tooltip";
import {NgTemplateOutlet, PercentPipe} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {ValgtittelPlassering} from "../spørreundersøkelse-spørsmål-skjema/spørreundersøkelse-spørsmål-skjema";
import {MatIcon} from "@angular/material/icon";
import {cubehelixLyshetForFarge, gjørFargeLysere} from "felles-ng2/støttefunksjoner/fargejustering";

const debug = initDebugger('RangeringResultaterSøylediagramKomponent');

/**
 * Viser resultater fra en undersøkelse for et spørsmål som et diagram
 */
@Component({
    selector: 'rangering-resultater-soeylediagram',
    templateUrl: 'rangering-resultater-søylediagram.html',
    styleUrl: 'rangering-resultater-søylediagram.scss',
    host: {
        'role': 'figure',
        '[attr.aria-label]': 'ariaLabel()',
        '[attr.aria-labelledby]': 'ariaLabelledBy()'
    },
    imports: [
        MatTooltip,
        PercentPipe,
        NgTemplateOutlet,
        TranslateModule,
        MatIcon
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RangeringResultaterSøylediagramKomponent {

    /** Liste med {@link RangeringAggregatOgTittel} som skal vises i diagrammet.
     * For hvert unike alternativ på tvers av resultater lages en søylegruppe der denne inneholder en søyle for hvert RangeringAggregatOgTittel.
     * Det første innslaget i resultater regnes som det dominerende og vises bredere enn de øvrige */
    resultater = input.required<RangeringAggregatOgTittel[]>();
    oppsett = input<SøylediagramOppsett>(stdSøylediagramOppsett());
    ariaLabel = input<string | null>(null);
    ariaLabelledBy = input<string | null>(null);

    format = computed(() => this.oppsett()?.format ?? 'staaende');
    visValgtittelOver = computed(() => this.oppsett()?.visValgtittel != false && this.oppsett()?.valgtittelPlassering == 'over');
    visValgtittelUnder = computed(() => this.oppsett()?.visValgtittel != false && this.oppsett()?.valgtittelPlassering != 'over');
    diagramHoeydePx = computed(() => this.oppsett()?.diagramHoeydePx ?? (this.format() == 'staaende' ? 100 : 60));
    staaendeData = computed(() => this.format() == 'staaende' ? this.beregnStåendeData() : []);
    liggendeKombinertData = computed(() => this.format() == 'liggendeKombinert' ? this.beregnLiggendeKombinertData() : []);

    constructor(public readonly visValgtittel: VisValgtittel) {
    }

    visValgtitlerIDiagram() {
        this.visValgtittel.veksle();
    }

    /** Returnerer bakgrunnsfarge for angitt alternativ, dersom ikkePrimærUndersøkelse returneres en lett gjennomskinnelig versjon */
    bakgrunnsfargeForValg(alternativ: RangeringValg, ikkePrimærUndersøkelse: boolean): FargeHex {
        //MERK ved endring av lyshet bør tilsvarende endring gjøres i lederplan-tjenester/src/main/resources/no/systemfabrikken/lederplan/tjenester/tekstmal/maler/generertepdfdokumenter/spørreundersøkelse_resultater_dokument.ftlx
        const farge = alternativ.farge;
        return ikkePrimærUndersøkelse ? gjørFargeLysere(farge, 0.5) : farge;
    }

    /** Returnerer tekstfarge, for mørke bakgrunnsfarger benytter vi hvit tekst, ellers sort */
    tekstfargePaaBakgrunnsfarge(bakgrunnsfarge: FargeHex): FargeHex {
        //MERK ved endring av terskel bør tilsvarende endring gjøres i lederplan-tjenester/src/main/resources/no/systemfabrikken/lederplan/tjenester/tekstmal/maler/generertepdfdokumenter/spørreundersøkelse_resultater_dokument.ftlx
        return cubehelixLyshetForFarge(bakgrunnsfarge) < 0.45 ? '#ffffff' : '#000000'; //sørger for hvit tekst på mørk bakgrunn
    }

    //private hjelpemetoder

    private beregnStåendeData(): StåendeData[] {
        const unikeAlternativer = this.unikeAlternativer();
        const antallUndersøkelser = this.resultater().length;
        const søylebreddeProsent = 100 / (antallUndersøkelser + 1);
        const søylekontainerbreddePx = 100 / (unikeAlternativer.length);

        return unikeAlternativer.map(alternativ => {
            return {
                alternativ,
                kontainerbreddePx: søylekontainerbreddePx,
                resultat: this.resultater().map((resultat, indeks) => {
                    //totalt antall respondenter i resultat
                    const totalt = this.antallRespondenter(resultat);
                    //antall respondenter som har valgt dette alternativet
                    const antall = resultat.aggregat.fordeling.filter(f => f.alternativ.verdi == alternativ.verdi).reduce((sum, f) => sum + f.antall, 0);
                    return {
                        id: resultat.id,
                        antall: antall,
                        avTotalt: totalt,
                        prosent: totalt > 0 ? antall / totalt : 0,
                        tittel: resultat.tittel,
                        tittelKort: resultat.tittelKort,
                        breddeProsent: indeks == antallUndersøkelser - 1 ? søylebreddeProsent * 2 : søylebreddeProsent,
                        hjelpetekst: alternativ.tittel + (antallUndersøkelser > 1 ? ` (${resultat.tittel})` : '')
                    }
                })
            }
        });
    }

    private beregnLiggendeKombinertData(): LiggendeKombinertData[] {
        const antallUndersøkelser = this.resultater().length;
        const søylehøydePx = this.diagramHoeydePx() / (antallUndersøkelser + 1); //+1 fordi dominerende (første resultat) skal være 2x høyden til ikke-dominerende
        return this.resultater().map((resultat, indeks) => {
            const dominerendeResultat = indeks == 0;
            const antallRespondenter = this.antallRespondenter(resultat);
            return {
                id: resultat.id,
                tittel: resultat.tittel,
                tittelKort: resultat.tittelKort,
                hoeydePx: søylehøydePx * (dominerendeResultat ? 2 : 1),
                ingenData: resultat.aggregat.fordeling.every(svar => svar.antall == 0),
                alternativer: resultat.aggregat.fordeling.map(alternativOgAntall => {
                    return {
                        alternativ: alternativOgAntall,
                        prosent: antallRespondenter > 0 ? alternativOgAntall.antall / antallRespondenter : 0,
                        hjelpetekst: alternativOgAntall.alternativ.tittel + (antallUndersøkelser > 1 ? ` (${resultat.tittel})` : '')
                    }
                }),
            }
        });
    }

    /** Totalt antall respondenter som har besvart spørsmålet, se {@link AlternativOgAntallModell#totaltAntallRespondenter}.
     * MERK at dette ikke kan beregnes som summen av antall for alternativene, da respondentene for spørsmål av typen velgNoenAvFlere
     * kan velge flere alternativer. Må være i synk med beregningen i spørreundersøkelse_resultater_dokument.ftlx (pdf),
     * se UndersøkelseResponsAggregater.AlternativOgAntall#prosentAvTotaltAntallRespondenter */
    private antallRespondenter(resultat: RangeringAggregatOgTittel): number {
        return Math.max(0, ...resultat.aggregat.fordeling.map(f => f.totaltAntallRespondenter));
    }

    private unikeAlternativer(): RangeringValg[] {
        return this.resultater().flatMap(resultat => resultat.aggregat.fordeling
          .map(f => f.alternativ))
          .reduce((unikeAlternativer, alternativ) => {
              if (!unikeAlternativer.some(eksisterende => eksisterende.verdi == alternativ.verdi))
                  unikeAlternativer.push(alternativ);
              return unikeAlternativer;
          }, [] as RangeringValg[]);
    }
}

/** Datastruktur som understøtter diagram av typen 'staaende' */
type StåendeData = {
    alternativ: RangeringValg;
    kontainerbreddePx: number;
    resultat: {
        id: string;
        antall: number;
        avTotalt: number;
        prosent: number;
        tittel: string;
        tittelKort: string;
        breddeProsent: number;
        hjelpetekst: string;
    }[];
}

/** Datastruktur som understøtter diagram av typen 'liggendeKombinert' */
type LiggendeKombinertData = {
    /** id som unikt identifiserer denne datasamlingen. Brukes kun i @for trackBy */
    id: string;
    tittel: string;
    tittelKort: string;
    hoeydePx: number;
    ingenData: boolean;
    alternativer: {
        alternativ: AlternativOgAntallModell;
        prosent: number;
        hjelpetekst: string;
    }[];
}

export type RangeringAggregatOgTittel = {
    /** id som unikt identifiserer dette aggregatet. Brukes kun i @for trackBy */
    id: string;
    /** Fulle tittelen til dette aggregatet */
    tittel: string;
    /** Tittel for dette aggregatet, brukes for å synliggjøre forskjellen mellom RangeringAggregatOgTittel innslag dersom flere er angitt i {@link RangeringResultaterSøylediagramKomponent#resultater}.
     * Merk at denne kun vises dersom {@link RangeringResultaterSøylediagramKomponent#resultater} inneholder mer enn ett innslag */
    tittelKort: string;
    /** Aggregerte resultater for spørsmål */
    aggregat: FordelingAggregatModell;
}

export function stdSøylediagramOppsett(): SøylediagramOppsett {
    return {valgtittelPlassering: 'under', format: 'staaende', visValgtittel: true};
}

export type SøylediagramOppsett = {
    /** Hvorvidt diagrammet skal vises som stående søyler ('staaende'), med én søyle per alternativ,
     * eller som liggende søyler ('liggendeKombinert'), med én søyle per alternativ plassert etter hverandre liggende på én linje */
    format?: 'staaende' | 'liggendeKombinert';
    /** Hvorvidt tittel for alternativene skal vises */
    visValgtittel?: boolean;
    /** Hvor tittel for alternativene skal plasseres. Kun aktuell når format='staaende' */
    valgtittelPlassering?: ValgtittelPlassering;
    /** Høyden på diagrammet i piksler. Dersom ikke angitt brukes 100px når format='staaende' og 40px ellers. */
    diagramHoeydePx?: number;
}

/** Tjeneste som holder rede på hvorvidt valgtitler skal vises eller ei. */
@Injectable()
export class VisValgtittel {
    private _vises = signal(false);

    vis(vises: boolean) {
        this._vises.set(vises);
    }

    veksle() {
        this._vises.update(vises => !vises);
    }

    get vises(): Signal<boolean> {
        return this._vises.asReadonly();
    }
}
